import axios from 'axios';
import { Room, CreateRoomRequest, VotingSession, CreateVotingSessionRequest, CastVoteRequest, Vote, JoinRoomRequest, QueueItem } from '../types';
import { API_V1_URL } from '../config/apiConfig';

const api = axios.create({
  baseURL: API_V1_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Response error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    });

    return Promise.reject(error);
  }
);

// Room API
export const roomApi = {
  createRoom: async (roomData: CreateRoomRequest): Promise<{room: Room, participants: any[], user_id: string}> => {
    const response = await api.post('/rooms/', roomData);
    return response.data;
  },

  getRoom: async (roomId: string): Promise<{room: Room, participants: any[]}> => {
    const response = await api.get(`/rooms/${roomId}`);
    return response.data;
  },

  joinRoom: async (roomId: string, joinData: JoinRoomRequest): Promise<{user_id: string}> => {
    const response = await api.post(`/rooms/${roomId}/join`, joinData);
    return response.data;
  },

  leaveRoom: async (roomId: string, userId: string): Promise<void> => {
    await api.delete(`/rooms/${roomId}/leave`, { data: { user_id: userId } });
  },

  getRoomParticipants: async (roomId: string): Promise<any[]> => {
    const response = await api.get(`/rooms/${roomId}/participants`);
    return response.data;
  },

  // room本体とアクティブセッションを1回のリクエストで取得する（ポーリング用）
  getSnapshot: async (roomId: string): Promise<{
    room: Room;
    participants: any[];
    active_session: { session: VotingSession; votes: Vote[] } | null;
  }> => {
    const response = await api.get(`/rooms/${roomId}/snapshot`);
    return response.data;
  },

  endActiveSession: async (roomId: string): Promise<void> => {
    await api.delete(`/rooms/${roomId}/active-session`);
  },

  addToQueue: async (
    roomId: string,
    item: { title: string; description?: string; jira_key?: string }
  ): Promise<QueueItem[]> => {
    const response = await api.post(`/rooms/${roomId}/queue`, item);
    return response.data.story_queue;
  },

  removeFromQueue: async (roomId: string, itemId: string): Promise<QueueItem[]> => {
    const response = await api.delete(`/rooms/${roomId}/queue/${itemId}`);
    return response.data.story_queue;
  },

  changeModerator: async (roomId: string, userId: string): Promise<Room> => {
    const response = await api.patch(`/rooms/${roomId}/moderator`, { user_id: userId });
    return response.data.room;
  },
};

// Voting Session API
export const votingApi = {
  createSession: async (roomId: string, sessionData: CreateVotingSessionRequest): Promise<VotingSession> => {
    const response = await api.post(`/rooms/${roomId}/sessions`, sessionData);
    return response.data;
  },

  castVote: async (sessionId: string, voteData: CastVoteRequest): Promise<Vote> => {
    const response = await api.put(`/sessions/${sessionId}/vote`, voteData);
    return response.data;
  },

  removeVote: async (sessionId: string, userId: string): Promise<void> => {
    await api.delete(`/sessions/${sessionId}/vote`, { data: { user_id: userId } });
  },

  revealVotes: async (sessionId: string): Promise<void> => {
    await api.put(`/sessions/${sessionId}/reveal`);
  },

  resetVotes: async (sessionId: string): Promise<void> => {
    await api.post(`/sessions/${sessionId}/reset`);
  },

  getSessionResults: async (sessionId: string): Promise<{session: VotingSession, votes: Vote[]}> => {
    const response = await api.get(`/sessions/${sessionId}/results`);
    return response.data;
  },

  getSessionHistory: async (roomId: string): Promise<VotingSession[]> => {
    const response = await api.get(`/rooms/${roomId}/sessions`);
    return response.data;
  },
};

export default api;